import React from 'react';

/**
 * Inline SVG brand mark — the "Duet": two interlocking S-arcs. A teal human
 * arc and a terracotta AI arc curve around each other to form a subtle "S"
 * (Symphonate). No tile — the arcs sit on the surface. Matches the Direction B
 * design handoff's BrandMark (assets/symphonate-mark.svg); colours use the
 * shipped Direction B palette (deep teal #127c6d / terracotta #d96f43). Pass
 * `onDark` on dark surfaces to lift the teal for contrast.
 */
export function BrandMark({ size = 28, onDark = false, mono = false, className }) {
  // `mono` renders both arcs in the inherited text colour (currentColor) so the
  // mark stays legible on a solid brand-coloured surface (e.g. the active
  // Symphonate chip); the default is the two-tone teal/terracotta Duet.
  const tealStroke = mono ? 'currentColor' : onDark ? '#2fb8a5' : '#127c6d';
  const terraStroke = mono ? 'currentColor' : '#d96f43';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-label="Symphonate mark"
      className={className}
      style={{ flexShrink: 0, display: 'block' }}
    >
      <path
        d="M44 18 A14 14 0 1 0 30 32"
        stroke={tealStroke}
        strokeWidth="9"
        strokeLinecap="round"
      />
      <path
        d="M20 46 A14 14 0 1 0 34 32"
        stroke={terraStroke}
        strokeWidth="9"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Integrated wordmark — the Duet arcs ARE the "S", so the mark + "ymphonate"
 * read as one word. Text renders in solid --brand-solid (#127c6d) — no
 * gradient. `withMark={false}` falls back to the plain "Symphonate" text.
 * `beta` appends a small "Beta" badge (terracotta) to flag the pre-launch stage.
 */
export function Wordmark({ size = '1.25rem', withMark = true, onDark = false, beta = false }) {
  const color = onDark ? '#fafaf7' : 'var(--brand-solid, #127c6d)';
  return (
    <span
      role="img"
      aria-label={beta ? 'Symphonate (Beta)' : 'Symphonate'}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '0.05em',
        fontSize: size,
        fontWeight: 700,
        lineHeight: 1,
        color,
        letterSpacing: '-0.02em',
      }}
    >
      {withMark && (
        <svg
          width="0.51em"
          height="0.78em"
          viewBox="10.5 -1.5 43 68"
          fill="none"
          aria-hidden="true"
          style={{ flexShrink: 0, display: 'block' }}
        >
          <path
            d="M44 18 A14 14 0 1 0 30 32"
            stroke={onDark ? '#2fb8a5' : '#127c6d'}
            strokeWidth="11"
            strokeLinecap="round"
          />
          <path
            d="M20 46 A14 14 0 1 0 34 32"
            stroke="#d96f43"
            strokeWidth="11"
            strokeLinecap="round"
          />
        </svg>
      )}
      <span>{withMark ? 'ymphonate' : 'Symphonate'}</span>
      {beta && (
        <sup
          aria-hidden="true"
          style={{
            fontSize: '0.34em',
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: onDark ? '#fafaf7' : '#d96f43',
            background: onDark ? 'rgba(217,111,67,0.9)' : 'rgba(217,111,67,0.12)',
            border: `1px solid ${onDark ? 'rgba(250,250,247,0.5)' : 'rgba(217,111,67,0.35)'}`,
            borderRadius: '0.4em',
            padding: '0.25em 0.5em',
            marginLeft: '0.35em',
            transform: 'translateY(-0.2em)',
            display: 'inline-block',
          }}
        >
          Beta
        </sup>
      )}
    </span>
  );
}

export default BrandMark;
